import { useCallback, useState } from 'react';
import { uploadCommentPhoto } from '@apis/checklists';
import type { ITaskAttachment } from '@interfaces/IScheduler';
import { photoRefusal, uploadRefusal, type PhotoRefusal } from '../lib/photoRefusal';

/** The task a comment's photos are stored under, on its loaded schedule. */
export interface ICommentPhotoTarget {
  siteId: string;
  scheduleNumber: string;
  taskId: string;
}

/** One picked photo: the local file (its preview) and, once stored, the server's attachment. */
export interface IPendingCommentPhoto {
  key: string;
  file: File;
  stored?: ITaskAttachment;
}

interface IScopedPhoto extends IPendingCommentPhoto {
  /** The task it was picked on — the drawer outlives a task switch. */
  scope: string;
}

export interface ICommentPhotoUploads {
  /** This task's picked photos, in pick order — stored or still uploading. */
  pending: IPendingCommentPhoto[];
  /** The stored attachments — what a comment may carry. */
  attachments: ITaskAttachment[];
  /** Some photo is still on its way up (the comment must wait for it). */
  uploading: boolean;
  /** Why the last photo picked on this task was dropped — a type or size the
   *  server does not store (refused before uploading), or a failed upload. */
  failure: PhotoRefusal | null;
  /** Upload `files` now — undefined with nowhere to store them (no loaded schedule). */
  add: ((files: File[]) => void) | undefined;
  remove: (key: string) => void;
  /** Forget every picked photo (the comment was posted). */
  reset: () => void;
}

const scopeOf = (t: ICommentPhotoTarget | undefined): string =>
  t ? `${t.siteId}/${t.scheduleNumber}/${t.taskId}` : '';

/**
 * A comment's photos, uploaded the moment they are picked to the task's
 * evidence store, so the comment carries what the server stored — an API path
 * every reader can fetch — and never a tab-local `blob:` URL.
 */
export function useCommentPhotoUploads(target: ICommentPhotoTarget | undefined): ICommentPhotoUploads {
  const [photos, setPhotos] = useState<IScopedPhoto[]>([]);
  const [refused, setRefused] = useState<{ scope: string; reason: PhotoRefusal } | null>(null);
  const scope = scopeOf(target);

  const upload = useCallback(async (to: ICommentPhotoTarget, photo: IScopedPhoto) => {
    try {
      const stored = await uploadCommentPhoto(to.siteId, to.scheduleNumber, to.taskId, photo.file, photo.file.name);
      setPhotos((list) => list.map((p) => (p.key === photo.key ? { ...p, stored } : p)));
    } catch (error) {
      setPhotos((list) => list.filter((p) => p.key !== photo.key));
      setRefused({ scope: photo.scope, reason: uploadRefusal(error) });
    }
  }, []);
  const remove = useCallback((key: string) => setPhotos((list) => list.filter((p) => p.key !== key)), []);
  const reset = useCallback(() => setPhotos([]), []);

  const add = target && ((files: File[]) => {
    // A file the server would refuse is said so now, not after an upload.
    const reasons = files.map(photoRefusal);
    const firstRefusal = reasons.find((reason) => reason !== null);
    setRefused(firstRefusal ? { scope, reason: firstRefusal } : null);
    const picked = files
      .filter((_, i) => reasons[i] === null)
      .map((file) => ({ key: crypto.randomUUID(), file, scope }));
    setPhotos((list) => [...list, ...picked]);
    for (const photo of picked) void upload(target, photo);
  });

  const pending = photos.filter((p) => p.scope === scope);
  return {
    pending,
    attachments: pending.flatMap((p) => (p.stored ? [p.stored] : [])),
    uploading: pending.some((p) => !p.stored),
    failure: refused?.scope === scope ? refused.reason : null,
    add,
    remove,
    reset,
  };
}
